import { useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { colors, radius, shadow } from '../theme';

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(username, password);
      navigate('/queues', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <div style={page}>
      <div style={brandPanel}>
        <div style={logoRow}>
          <span style={logoMark}>E2</span>
          <span style={logoText}>Enable 2.0</span>
        </div>
        <h2 style={brandHeadline}>Patient Financial Advocacy Platform</h2>
        <p style={brandSub}>
          Role-first work queues, structured workflows, and advisory AI — built for the advocate team
          on a unified, multi-tenant platform.
        </p>
        <div style={brandFooter}>Crosslake AI Labs · for Panorama / PFA</div>
      </div>

      <div style={formPanel}>
        <form onSubmit={handleSubmit} style={card}>
          <h1 style={title}>Staff Console</h1>
          <p style={subtitle}>
            Sign in with the demo account ({import.meta.env.VITE_DEMO_EMAIL ?? 'demo@enable.health'})
          </p>
          {error && <div style={errorBox}>{error}</div>}
          <label style={label}>
            Username
            <input
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
            />
          </label>
          <label style={label}>
            Password
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading || !username || !password}
            style={{ marginTop: 4 }}
          >
            {isLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

const page: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  minHeight: '100vh',
  background: colors.pageBg,
};
const brandPanel: CSSProperties = {
  background: colors.brand,
  color: '#FFFFFF',
  padding: '56px 48px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};
const logoRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 36 };
const logoMark: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: radius.sm,
  background: colors.brandAccent,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 800,
  fontSize: '1rem',
};
const logoText: CSSProperties = { fontWeight: 700, fontSize: '1.3rem' };
const brandHeadline: CSSProperties = { color: '#FFFFFF', fontSize: '1.7rem', fontWeight: 700, margin: '0 0 14px', maxWidth: 420 };
const brandSub: CSSProperties = { color: 'rgba(255,255,255,0.78)', fontSize: '1rem', lineHeight: 1.6, maxWidth: 440 };
const brandFooter: CSSProperties = { marginTop: 40, fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' };
const formPanel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
const card: CSSProperties = {
  width: '100%',
  maxWidth: 380,
  background: '#FFFFFF',
  borderRadius: radius.lg,
  border: `1px solid ${colors.border}`,
  boxShadow: shadow.lg,
  padding: 32,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const title: CSSProperties = { fontSize: '1.4rem', fontWeight: 700, margin: 0, color: colors.textPrimary };
const subtitle: CSSProperties = { fontSize: '0.9rem', color: colors.textSecondary, margin: 0 };
const label: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: '0.88rem',
  fontWeight: 600,
  color: colors.textPrimary,
};
const errorBox: CSSProperties = {
  padding: '10px 12px',
  background: '#FEE2E2',
  border: '1px solid #FCA5A5',
  borderRadius: radius.sm,
  fontSize: '0.86rem',
  color: '#991B1B',
};
